import { useState } from "react";

import {
  type E2EAgentRunCheckDetails,
  useE2EAgentRunCheckDetails,
} from "../api/admin";
import { CHECK_SEVERITY_PRESENTATION } from "../lib/runChecks";
import {
  Button,
  Search,
  Status,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui";

export function RunCheckFailureList({
  details,
}: {
  details: E2EAgentRunCheckDetails;
}) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const failures = (details.failures ?? []).filter((failure) =>
    [
      failure.system,
      failure.name,
      failure.severity,
      ...(failure.examples ?? []).flatMap((example) => [
        example.message,
        example.location,
      ]),
    ].some((value) => value?.toLowerCase().includes(query)),
  );
  return (
    <div className="e2e-run-check-details">
      <p>Final-attempt checks, not a revalidation of model edits.</p>
      <Search
        aria-label="Search failed checks"
        placeholder="Search checks, messages, or locations"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {details.truncated && (
        <p role="status">Only the first 200 check groups are shown.</p>
      )}
      {failures.length > 0 ? (
        <TableContainer>
          <Table className="e2e-run-check-table" aria-label="Failed checks">
            <TableHead>
              <TableRow>
                <TableHeader>System</TableHeader>
                <TableHeader>Check and retained details</TableHeader>
                <TableHeader>Level</TableHeader>
                <TableHeader>Findings</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {failures.map((failure, index) => {
                const presentation =
                  CHECK_SEVERITY_PRESENTATION[failure.severity];
                const examples = failure.examples ?? [];
                return (
                  <TableRow key={`${failure.system}-${failure.name}-${index}`}>
                    <TableCell>{failure.system.toUpperCase()}</TableCell>
                    <TableCell>
                      <details>
                        <summary>{failure.name}</summary>
                        {failure.status === "error" && (
                          <p>Checker execution error</p>
                        )}
                        {examples.length > 0 ? (
                          <ul className="e2e-run-check-examples">
                            {examples.map((example, exampleIndex) => (
                              <li key={exampleIndex}>
                                <div>
                                  {example.message || "No message retained."}
                                </div>
                                {example.location && (
                                  <small>{example.location}</small>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No examples were retained for this check.</p>
                        )}
                        {failure.examples_truncated && (
                          <p>
                            Showing {examples.length} retained examples;
                            additional findings or locations are not shown.
                          </p>
                        )}
                      </details>
                    </TableCell>
                    <TableCell>
                      <Status tone={presentation.tone}>
                        {presentation.label}
                      </Status>
                    </TableCell>
                    <TableCell>
                      {failure.count?.toLocaleString() ?? "Unavailable"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <p>
          {query
            ? "No checks match your search."
            : "No failure details were retained."}
        </p>
      )}
      <p>
        Full retained reports are available in Immutable artifacts. Missing
        examples do not mean checks passed.
      </p>
    </div>
  );
}

export default function RunCheckDetails({ runId }: { runId: string }) {
  const query = useE2EAgentRunCheckDetails(runId);
  if (query.isPending)
    return <div className="state">Loading failed checks…</div>;
  if (query.error)
    return (
      <div className="state err" role="alert">
        Failed-check details could not be loaded.{" "}
        <Button tone="quiet" size="sm" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    );
  return query.data ? <RunCheckFailureList details={query.data} /> : null;
}
